import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { supplierKeys } from "@/lib/queryKeys";
import { getSupplierSpendSummary } from "@/services/supplierService";

function formatMoney(
  currency: string,
  amount: number,
  locale: string,
): string {
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  return `${sign}${currency} ${abs.toLocaleString(locale === "es" ? "es-UY" : "en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const MONTH_LABELS_ES = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

function formatMonthLabel(ym: string): string {
  const [yearStr, monthStr] = ym.split("-");
  const monthIdx = Number(monthStr) - 1;
  const monthLabel = MONTH_LABELS_ES[monthIdx] ?? monthStr ?? "";
  return `${monthLabel} ${(yearStr ?? "").slice(2)}`;
}

/**
 * KPI display rule:
 *  - Only USD has activity (export-services supplier paid in USD) →
 *    headline USD so the prominent number reflects real activity
 *    instead of a misleading "UYU 0,00".
 *  - Otherwise headline UYU (primary local currency for most UY
 *    tenants). When USD also has activity, show it as subtitle so
 *    multi-currency suppliers stay legible at a glance.
 *
 * Returned `subtitleKind`:
 *  - 'amount' → render the secondary currency amount
 *  - 'noOther' → render the appropriate "Sin {currency} este año" hint
 *  - 'inactive' → render the "sin actividad" hint (both zero)
 */
type KpiDisplay =
  | {
      headline: { currency: "UYU" | "USD"; amount: number };
      subtitleKind: "amount";
      subtitle: { currency: "UYU" | "USD"; amount: number };
    }
  | {
      headline: { currency: "UYU" | "USD"; amount: number };
      subtitleKind: "noOther";
      missingCurrency: "UYU" | "USD";
    }
  | {
      headline: { currency: "UYU" | "USD"; amount: number };
      subtitleKind: "inactive";
    };

function pickKpiCurrencies(uyu: number, usd: number): KpiDisplay {
  if (uyu === 0 && usd === 0) {
    return {
      headline: { currency: "UYU", amount: 0 },
      subtitleKind: "inactive",
    };
  }
  if (uyu === 0 && usd !== 0) {
    return {
      headline: { currency: "USD", amount: usd },
      subtitleKind: "noOther",
      missingCurrency: "UYU",
    };
  }
  if (usd === 0) {
    return {
      headline: { currency: "UYU", amount: uyu },
      subtitleKind: "noOther",
      missingCurrency: "USD",
    };
  }
  return {
    headline: { currency: "UYU", amount: uyu },
    subtitleKind: "amount",
    subtitle: { currency: "USD", amount: usd },
  };
}

export function SupplierSummaryTab({
  supplierId,
}: {
  supplierId: string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const year = new Date().getFullYear();

  const { data, isLoading, isError } = useQuery({
    queryKey: supplierKeys.spendSummary(supplierId),
    queryFn: () => getSupplierSpendSummary(supplierId),
    staleTime: 60_000,
  });

  // Bar height normalization across both currencies so the chart's
  // tallest column is always 100%. USD bars are still rendered as USD
  // values — we just scale the visual height by max(UYU+USD).
  const maxBar = useMemo(() => {
    if (!data) return 1;
    return Math.max(
      1,
      ...data.monthly.map((b) =>
        Math.max(
          b.recibidasUyu + b.expensesUyu,
          (b.recibidasUsd + b.expensesUsd) * 40,
        ),
      ),
    );
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <LoadingLogo size="md" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        title={t(
          "suppliers.detail.summaryCard.errorTitle",
          "No pudimos cargar el resumen",
        )}
        message={t(
          "suppliers.detail.summaryCard.errorHint",
          "Reintentá en unos segundos.",
        )}
      />
    );
  }

  const { ytd, monthly, topCategoriesYtd, allTime, pendingAckCount } = data;
  const recibidasKpi = pickKpiCurrencies(ytd.recibidasUyu, ytd.recibidasUsd);
  const expensesKpi = pickKpiCurrencies(ytd.expensesUyu, ytd.expensesUsd);
  const ivaKpi = pickKpiCurrencies(ytd.ivaCreditUyu, ytd.ivaCreditUsd);

  const renderSubtitle = (kpi: KpiDisplay): string => {
    if (kpi.subtitleKind === "amount") {
      return formatMoney(kpi.subtitle.currency, kpi.subtitle.amount, locale);
    }
    if (kpi.subtitleKind === "noOther") {
      return kpi.missingCurrency === "USD"
        ? t("suppliers.detail.summary.noUsd", "Sin USD este año")
        : t("suppliers.detail.summary.noUyu", "Sin UYU este año");
    }
    return t(
      "suppliers.detail.summary.noSecondaryCurrency",
      "Sin actividad este año",
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>
              {t(
                "suppliers.detail.summary.ytdSpendLabel",
                "Gasto {{year}} (recibidas)",
                { year },
              )}
            </CardDescription>
            <CardTitle className="text-xl tabular-nums">
              {formatMoney(
                recibidasKpi.headline.currency,
                recibidasKpi.headline.amount,
                locale,
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 text-xs text-muted-foreground tabular-nums">
            {renderSubtitle(recibidasKpi)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>
              {t(
                "suppliers.detail.summary.ytdExpensesLabel",
                "Gastos manuales {{year}}",
                { year },
              )}
            </CardDescription>
            <CardTitle className="text-xl tabular-nums">
              {formatMoney(
                expensesKpi.headline.currency,
                expensesKpi.headline.amount,
                locale,
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 text-xs text-muted-foreground tabular-nums">
            {renderSubtitle(expensesKpi)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>
              {t(
                "suppliers.detail.summary.ytdIvaLabel",
                "IVA crédito {{year}}",
                { year },
              )}
            </CardDescription>
            <CardTitle className="text-xl tabular-nums">
              {formatMoney(
                ivaKpi.headline.currency,
                ivaKpi.headline.amount,
                locale,
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 text-xs text-muted-foreground tabular-nums">
            {renderSubtitle(ivaKpi)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>
              {t(
                "suppliers.detail.summary.recordCountsLabel",
                "Registros (histórico)",
              )}
            </CardDescription>
            <CardTitle className="text-xl tabular-nums">
              {allTime.recibidasCount + allTime.expensesCount}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 text-xs text-muted-foreground">
            {t(
              "suppliers.detail.summary.recordCountsBreakdown",
              "{{recibidas}} recibidas · {{expenses}} gastos",
              {
                recibidas: allTime.recibidasCount,
                expenses: allTime.expensesCount,
              },
            )}
          </CardContent>
        </Card>
      </div>

      {pendingAckCount > 0 ? (
        <Card className="border-yellow-300 bg-yellow-50 dark:border-yellow-700/40 dark:bg-yellow-950/20">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle
              className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600"
              aria-hidden
            />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium text-foreground">
                {t(
                  "suppliers.detail.summary.pendingAckTitle",
                  "Tenés {{count}} comprobante(s) pendientes de aceptar de este proveedor",
                  { count: pendingAckCount },
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {t(
                  "suppliers.detail.summary.pendingAckHint",
                  "Si recibís comprobantes de este proveedor con frecuencia, activá la aceptación automática para ahorrar tiempo.",
                )}
              </div>
            </div>
            <Button asChild variant="outline" size="sm" className="shrink-0">
              <Link to={`/invoices/recibidas?supplierId=${supplierId}&pendingAck=true`}>
                {t(
                  "suppliers.detail.summary.pendingAckCta",
                  "Ver pendientes",
                )}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t(
              "suppliers.detail.summary.monthlyTitle",
              "Gasto últimos 12 meses",
            )}
          </CardTitle>
          <CardDescription>
            {t(
              "suppliers.detail.summary.monthlyHint",
              "Combinado recibidas + gastos manuales. USD se escala visualmente para que sea comparable, pero los totales se muestran por separado.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-1 sm:gap-2">
            {monthly.map((b) => {
              const uyuTotal = b.recibidasUyu + b.expensesUyu;
              const usdTotal = b.recibidasUsd + b.expensesUsd;
              const heightPct =
                Math.max(uyuTotal, usdTotal * 40) / maxBar;
              return (
                <div
                  key={b.month}
                  className="flex flex-col items-center justify-end gap-1"
                  title={`${formatMoney("UYU", uyuTotal, locale)}${usdTotal !== 0 ? ` · ${formatMoney("USD", usdTotal, locale)}` : ""}`}
                >
                  <div className="flex h-32 w-full items-end">
                    <div
                      className="w-full rounded-sm bg-primary/70"
                      style={{ height: `${heightPct * 100}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {formatMonthLabel(b.month)}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t(
              "suppliers.detail.summary.categoriesTitle",
              "Top categorías de gasto {{year}}",
              { year },
            )}
          </CardTitle>
          <CardDescription>
            {t(
              "suppliers.detail.summary.categoriesHint",
              "Solo aplica a gastos manuales con categoría asignada.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {topCategoriesYtd.length === 0 ? (
            <EmptyState
              title={t(
                "suppliers.detail.summary.categoriesEmpty",
                "Sin gastos manuales este año.",
              )}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t(
                      "suppliers.detail.summary.colCategory",
                      "Categoría",
                    )}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("suppliers.detail.summary.colUyu", "UYU")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("suppliers.detail.summary.colUsd", "USD")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("suppliers.detail.summary.colCount", "#")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topCategoriesYtd.map((c) => (
                  <TableRow key={c.category}>
                    <TableCell className="font-medium">
                      {c.category}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {c.amountUyu !== 0
                        ? formatMoney("UYU", c.amountUyu, locale)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {c.amountUsd !== 0
                        ? formatMoney("USD", c.amountUsd, locale)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {c.count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
